import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { CoreStaticDataService } from '../Component/Api/StaticData/CoreStaticDataService';
import { ElementWithDataPagesDto, DashboardNavDto } from '../Component/Model/Dto/StaticData';
import { BBBEEScorecardAtom } from '../State/BBBEEScorecard.Atom';
import { ProtectedRoute, portal, bbbeeScorecardRoute } from '../Component/Common/Constants/routes';
import { LegacyBEEPage } from '../Component/Common/Components/LegacyBEEPage/LegacyBEEPage';
import {
    legacyRouteMap,
    elementIcons,
    getFixedTopRoutes,
    getFixedBottomRoutes,
} from './legacyRouteConfig';

export const useBEELegacyPages = () => {
    const [routes, setRoutes] = useState<ProtectedRoute[]>([]);
    const [loading, setLoading] = useState(true);
    const scorecard = useRecoilValue(BBBEEScorecardAtom);

    useEffect(() => {
        const fetchDataPages = async () => {
            if (!scorecard.scorecardTypeId) {
                setRoutes([]);
                setLoading(false);
                return;
            }

            setLoading(true);

            try {
                const response = await CoreStaticDataService.getElementsWithDataPages(scorecard.scorecardTypeId);
                const elements = response.data;

                const dynamicRoutes: ProtectedRoute[] = elements.map((element: ElementWithDataPagesDto) => {
                    const elementName = element.name;
                    const baseLegacyRoute = legacyRouteMap[elementName] || elementName.toLowerCase().replace(/\s+/g, '');
                    const icon = elementIcons[elementName] || 'nc-icon nc-single-copy-04';
                    const views: any[] = [];

                    if (element.dashboards && element.dashboards.length > 0) {
                        if (element.dashboards.length === 1) {
                            const dashboard = element.dashboards[0];
                            views.push({
                                path: `${bbbeeScorecardRoute}${baseLegacyRoute}/dashboard`,
                                name: 'Dashboard',
                                mini: 'DB',
                                Component: LegacyBEEPage,
                                componentProps: {
                                    legacyRoute: `scorecard/{ScorecardId}/${dashboard.url}`,
                                    elementName: elementName,
                                    pageTitle: 'Dashboard'
                                },
                                layout: portal,
                            });
                        } else {
                            const dashboardTabs = element.dashboards.map((db: DashboardNavDto) => ({
                                label: db.name,
                                route: db.name.toLowerCase().replace(/\s+/g, '-'),
                                iframeRoute: `scorecard/{ScorecardId}/${db.url}`
                            }));

                            views.push({
                                path: `${bbbeeScorecardRoute}${baseLegacyRoute}/dashboard`,
                                name: 'Dashboards',
                                mini: 'DB',
                                Component: LegacyBEEPage,
                                componentProps: {
                                    legacyRoute: `scorecard/{ScorecardId}/${element.dashboards[0].url}`,
                                    basePath: `${portal}${bbbeeScorecardRoute}${baseLegacyRoute}/dashboard`,
                                    tabs: dashboardTabs,
                                    elementName: elementName,
                                    pageTitle: 'Dashboard'
                                },
                                layout: portal,
                            });
                        }
                    }

                    if (element.scorecardUrl) {
                        views.push({
                            path: `${bbbeeScorecardRoute}${baseLegacyRoute}/scorecard`,
                            name: 'Scorecard',
                            mini: 'SC',
                            Component: LegacyBEEPage,
                            componentProps: {
                                legacyRoute: `scorecard/{ScorecardId}/${element.scorecardUrl}`,
                                elementName: elementName,
                                pageTitle: 'Scorecard'
                            },
                            layout: portal,
                        });
                    }

                    if (element.dataPages && element.dataPages.length > 0) {
                        if (element.dataPages.length === 1) {
                            const dataPage = element.dataPages[0];
                            views.push({
                                path: `${bbbeeScorecardRoute}${baseLegacyRoute}/datapage`,
                                name: 'Data Page',
                                mini: 'DP',
                                Component: LegacyBEEPage,
                                componentProps: { legacyRoute: dataPage.url, elementName: elementName, pageTitle: 'Data Page' },
                                layout: portal,
                            });
                        } else {
                            const tabs = element.dataPages.map(dp => {
                                const urlParts = dp.url.split('/').filter(p => p && !p.startsWith('#') && p !== '{ScorecardId}');
                                const tabRoute = urlParts[urlParts.length - 1] || dp.name.toLowerCase().replace(/\s+/g, '');
                                return {
                                    label: dp.name,
                                    route: tabRoute,
                                    iframeRoute: dp.url
                                };
                            });

                            views.push({
                                path: `${bbbeeScorecardRoute}${baseLegacyRoute}/datapage`,
                                name: 'Data Page',
                                mini: 'DP',
                                Component: LegacyBEEPage,
                                componentProps: {
                                    legacyRoute: element.dataPages[0].url,
                                    basePath: `${portal}${bbbeeScorecardRoute}${baseLegacyRoute}/datapage`,
                                    tabs: tabs,
                                    elementName: elementName,
                                    pageTitle: 'Data Page'
                                },
                                layout: portal,
                            });
                        }
                    }

                    return {
                        name: elementName,
                        icon: icon,
                        layout: portal,
                        isExternal: false,
                        collapse: true,
                        state: `${elementName.replace(/\s+/g, '')}Collapse`,
                        views: views,
                    };
                });

                const validRoutes = dynamicRoutes.filter(route => route.views && route.views.length > 0);

                const elementNames = elements.map((el: ElementWithDataPagesDto) => el.name);

                const allRoutes = [
                    ...getFixedTopRoutes(),
                    ...validRoutes,
                    ...getFixedBottomRoutes(elementNames),
                ];

                setRoutes(allRoutes);
            } catch (error) {
                console.log(error);
                setRoutes([]);
            } finally {
                setLoading(false);
            }
        };

        fetchDataPages();
    }, [scorecard.scorecardTypeId]);

    return { routes, loading };
};
